interface PixelContent {
  id: string;
  quantity?: number;
  item_price?: number;
}

interface TrackSearchPayload {
  query: string;
  retailer?: string;
}

interface TrackProductPayload {
  contentId: string;
  contentName?: string;
  contentCategory?: string;
  value?: number;
  currency?: string;
  quantity?: number;
}

interface TrackCheckoutPayload {
  value?: number;
  currency?: string;
  numItems?: number;
  contentIds?: string[];
  contents?: PixelContent[];
}

type TrackPurchasePayload = TrackCheckoutPayload;

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

const DEFAULT_CURRENCY = "USD";

const isBrowser = (): boolean => typeof window !== "undefined";

const hasFbq = (): boolean =>
  isBrowser() && typeof window.fbq === "function";

/**
 * Load the Facebook Pixel SDK and initialize with the pixel ID.
 * Call once on app startup (e.g. in App.tsx useEffect).
 */
export const initMetaPixel = () => {
  if (!isBrowser()) return;

  const pixelId = process.env.REACT_APP_META_PIXEL_ID;
  if (!pixelId) return;

  // Avoid double-init
  if (hasFbq()) return;

  /* eslint-disable */
  (function (f: any, b: Document, e: string, v: string) {
    const n: any = (f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    const t = b.createElement(e) as HTMLScriptElement;
    t.async = true;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    s.parentNode?.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */

  window.fbq!("init", pixelId);
  window.fbq!("track", "PageView");
};

const track = (eventName: string, params?: Record<string, unknown>) => {
  if (!hasFbq()) return;

  if (params && Object.keys(params).length > 0) {
    window.fbq?.("track", eventName, params);
    return;
  }

  window.fbq?.("track", eventName);
};

const normalizeCurrency = (currency?: string): string => {
  const normalized = currency?.trim().toUpperCase();
  return normalized || DEFAULT_CURRENCY;
};

const normalizeNumber = (value: unknown): number | undefined => {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return undefined;
  }

  return Number(value.toFixed(2));
};

const normalizeContents = (contents?: PixelContent[]) => {
  if (!contents?.length) return undefined;

  const normalized = contents
    .filter((item) => item.id)
    .map((item) => {
      const normalizedItem: Record<string, unknown> = { id: item.id };

      if (
        typeof item.quantity === "number" &&
        Number.isFinite(item.quantity) &&
        item.quantity > 0
      ) {
        normalizedItem.quantity = item.quantity;
      }

      const itemPrice = normalizeNumber(item.item_price);
      if (itemPrice !== undefined) {
        normalizedItem.item_price = itemPrice;
      }

      return normalizedItem;
    });

  return normalized.length > 0 ? normalized : undefined;
};

export const trackPageView = () => {
  track("PageView");
};

export const trackSearch = ({ query, retailer }: TrackSearchPayload) => {
  const searchQuery = query.trim();
  if (!searchQuery) return;

  const params: Record<string, unknown> = {
    search_string: searchQuery,
  };

  if (retailer) {
    params.content_category = retailer;
  }

  track("Search", params);
};

export const trackViewContent = ({
  contentId,
  contentName,
  contentCategory,
  value,
  currency,
}: TrackProductPayload) => {
  if (!contentId) return;

  const params: Record<string, unknown> = {
    content_ids: [contentId],
    content_type: "product",
  };

  if (contentName) {
    params.content_name = contentName;
  }

  if (contentCategory) {
    params.content_category = contentCategory;
  }

  const normalizedValue = normalizeNumber(value);
  if (normalizedValue !== undefined) {
    params.value = normalizedValue;
    params.currency = normalizeCurrency(currency);
  }

  track("ViewContent", params);
};

export const trackAddToCart = ({
  contentId,
  contentName,
  contentCategory,
  value,
  currency,
  quantity,
}: TrackProductPayload) => {
  if (!contentId) return;

  const params: Record<string, unknown> = {
    content_ids: [contentId],
    content_type: "product",
  };

  if (contentName) {
    params.content_name = contentName;
  }

  if (contentCategory) {
    params.content_category = contentCategory;
  }

  if (
    typeof quantity === "number" &&
    Number.isFinite(quantity) &&
    quantity > 0
  ) {
    params.num_items = quantity;
  }

  const normalizedValue = normalizeNumber(value);
  if (normalizedValue !== undefined) {
    params.value = normalizedValue;
    params.currency = normalizeCurrency(currency);
  }

  track("AddToCart", params);
};

export const trackInitiateCheckout = ({
  value,
  currency,
  numItems,
  contentIds,
  contents,
}: TrackCheckoutPayload) => {
  const params: Record<string, unknown> = {
    content_type: "product",
  };

  if (contentIds && contentIds.length > 0) {
    params.content_ids = contentIds;
  }

  if (typeof numItems === "number" && Number.isFinite(numItems) && numItems > 0) {
    params.num_items = numItems;
  }

  const normalizedValue = normalizeNumber(value);
  if (normalizedValue !== undefined) {
    params.value = normalizedValue;
    params.currency = normalizeCurrency(currency);
  }

  const normalizedContents = normalizeContents(contents);
  if (normalizedContents) {
    params.contents = normalizedContents;
  }

  track("InitiateCheckout", params);
};

export const trackPurchase = ({
  value,
  currency,
  numItems,
  contentIds,
  contents,
}: TrackPurchasePayload) => {
  const params: Record<string, unknown> = {};

  const normalizedValue = normalizeNumber(value);
  if (normalizedValue !== undefined) {
    params.value = normalizedValue;
    params.currency = normalizeCurrency(currency);
  }

  if (contentIds && contentIds.length > 0) {
    params.content_ids = contentIds;
    params.content_type = "product";
  }

  if (typeof numItems === "number" && Number.isFinite(numItems) && numItems > 0) {
    params.num_items = numItems;
  }

  const normalizedContents = normalizeContents(contents);
  if (normalizedContents) {
    params.contents = normalizedContents;
  }

  track("Purchase", params);
};

export const trackCompleteRegistration = (method?: string) => {
  if (method) {
    track("CompleteRegistration", { method });
    return;
  }

  track("CompleteRegistration");
};
